import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';

import countries110m from '_assets/data/countries-110m.json';

// Network-coverage tiers (business content, not derived from any API).
const TIER_NAVY = ['United States of America', 'Canada', 'Ireland', 'United Kingdom', 'Norway', 'Sweden', 'Finland', 'Denmark', 'Germany', 'Netherlands', 'Belgium', 'Switzerland', 'Austria', 'Czechia', 'Poland', 'Slovakia', 'Hungary', 'Slovenia', 'Croatia', 'Portugal', 'Spain', 'Italy', 'Greece', 'Romania', 'Bulgaria', 'Turkey', 'Saudi Arabia', 'United Arab Emirates', 'Qatar', 'India', 'China', 'Japan', 'Australia', 'New Zealand', 'Brazil', 'Chile', 'Ethiopia', 'South Africa', 'Egypt', 'Iceland', 'Estonia', 'Latvia', 'Lithuania', 'Vietnam'];
const TIER_MID = ['Russia', 'Mexico', 'France', 'Morocco', 'Algeria', 'Tunisia', 'Libya', 'Nigeria', 'Ghana', 'Kenya', 'Tanzania', 'Uganda', 'Zambia', 'Zimbabwe', 'Mozambique', 'Angola', 'Namibia', 'Botswana', 'Senegal', "Côte d'Ivoire", 'Cameroon', 'Indonesia', 'Malaysia', 'Philippines', 'Thailand', 'Singapore', 'Pakistan', 'Bangladesh', 'Oman', 'Kuwait', 'Jordan', 'Israel', 'Iraq', 'Iran', 'Ukraine', 'Belarus', 'Serbia', 'Colombia', 'Peru', 'Argentina', 'Ecuador', 'Cuba', 'Papua New Guinea'];
const TIER_LIGHT = ['Kazakhstan', 'Uzbekistan', 'Turkmenistan', 'Kyrgyzstan', 'Tajikistan', 'Mongolia', 'Nepal', 'Sri Lanka', 'Myanmar', 'Cambodia', 'Laos', 'Bolivia', 'Paraguay', 'Uruguay', 'Venezuela', 'Guyana', 'Suriname', 'Madagascar', 'Malawi', 'Rwanda', 'Burundi', 'Somalia', 'Eritrea', 'Chad', 'Niger', 'Mali', 'Mauritania', 'Guinea', 'Sierra Leone', 'Liberia', 'Benin', 'Togo', 'Burkina Faso', 'Gabon', 'Congo', 'Dem. Rep. Congo', 'Central African Rep.', 'South Sudan', 'Lesotho', 'Eswatini', 'Bosnia and Herz.', 'Albania', 'North Macedonia', 'Montenegro', 'Moldova', 'Georgia', 'Armenia', 'Azerbaijan', 'Syria', 'Lebanon', 'Yemen', 'Afghanistan', 'Bhutan', 'Sudan', 'W. Sahara', 'Greenland', 'Panama', 'Costa Rica', 'Nicaragua', 'Honduras', 'Guatemala', 'El Salvador', 'Belize', 'Haiti', 'Dominican Rep.', 'Jamaica', 'Trinidad and Tobago', 'Fiji', 'Solomon Is.', 'Vanuatu', 'New Caledonia', 'Timor-Leste', 'Brunei', 'Taiwan', 'South Korea', 'North Korea'];

const DROP = new Set(['Antarctica', 'Fr. S. Antarctic Lands']);
const NAVY = new Set(TIER_NAVY);
const MID = new Set(TIER_MID);
const LIGHT = new Set(TIER_LIGHT);

const fillFor = (feature) => {
  const name = feature.properties.name;
  if (NAVY.has(name)) return '#26296b';
  if (MID.has(name)) return '#3b83d8';
  if (LIGHT.has(name)) return '#93bfe8';
  return '#c2c4c9';
};

const WorldMap = () => {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const width = 1000;
    const height = 480;

    const svg = d3.select(host)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('aria-hidden', 'true')
      .style('display', 'block')
      .style('width', '100%')
      .style('height', 'auto');

    const features = topojson.feature(countries110m, countries110m.objects.countries).features
      .filter((feature) => !DROP.has(feature.properties.name));

    const projection = (d3.geoEqualEarth ? d3.geoEqualEarth() : d3.geoNaturalEarth1()).rotate([-10, 0, 0]);
    projection.fitExtent([[12, 10], [width - 12, height - 10]], { type: 'FeatureCollection', features });
    const path = d3.geoPath(projection);

    svg.append('g')
      .selectAll('path')
      .data(features)
      .join('path')
      .attr('d', path)
      .attr('fill', fillFor)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 0.6);

    return () => {
      host.replaceChildren();
    };
  }, []);

  return (
    <div
      ref={hostRef}
      role="img"
      aria-label="World map with countries shaded by PRM Assist network coverage."
      style={{ display: 'block', width: '100%', aspectRatio: '100 / 48' }}
    />
  );
};

export default WorldMap;
